import { useEffect } from "react";

/**
 * Lightweight per-page SEO manager: sets <title>, meta description,
 * Open Graph + Twitter cards, canonical URL, and optional JSON-LD structured data.
 * Cleans up on unmount so other pages don't inherit stale tags.
 */
export default function useSeo({ title, description, canonical, image, jsonLd } = {}) {
  useEffect(() => {
    const prevTitle = document.title;
    if (title) document.title = title;

    const created = [];
    const upsertMeta = (attr, key, content) => {
      if (content == null) return;
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute("content", content);
    };

    upsertMeta("name", "description", description);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:image", image);
    upsertMeta("property", "og:url", canonical || (typeof window !== "undefined" ? window.location.href : undefined));
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);

    let canonicalEl = null;
    if (canonical) {
      canonicalEl = document.head.querySelector('link[rel="canonical"]');
      if (!canonicalEl) {
        canonicalEl = document.createElement("link");
        canonicalEl.setAttribute("rel", "canonical");
        document.head.appendChild(canonicalEl);
        created.push(canonicalEl);
      }
      canonicalEl.setAttribute("href", canonical);
    }

    let jsonLdScript = null;
    if (jsonLd) {
      jsonLdScript = document.createElement("script");
      jsonLdScript.type = "application/ld+json";
      jsonLdScript.text = JSON.stringify(jsonLd);
      jsonLdScript.setAttribute("data-page-seo", "1");
      document.head.appendChild(jsonLdScript);
      created.push(jsonLdScript);
    }

    return () => {
      document.title = prevTitle;
      created.forEach((n) => n.parentNode && n.parentNode.removeChild(n));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, JSON.stringify(jsonLd || {})]);
}
